import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Edit, Plus, Shield, Trash, User as UserIcon, Search, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useUsersStore, type User } from "@/lib/stores/users";
import { CompleteDeletionDialog } from "./CompleteDeletionDialog";
import { removeUserCompletely } from "@/lib/utils/completeUserDeletion";
import { getAuthRedirectUrl } from "@/lib/utils/authRedirect";

const userFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
  role: z.enum(["admin", "doctor", "staff"]),
  specialty: z.string().optional(),
  registrationNumber: z.string().optional(),
}).superRefine((data, ctx) => {
  // Only validate password for new users
  if (!data.password && !data.confirmPassword) {
    return true;
  }

  if (data.password) {
    if (data.password.length < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password must be at least 8 characters",
        path: ["password"],
      });
    }
    if (!/[A-Z]/.test(data.password)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password must contain at least one uppercase letter",
        path: ["password"],
      });
    }
    if (!/[a-z]/.test(data.password)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password must contain at least one lowercase letter",
        path: ["password"],
      });
    }
    if (!/[0-9]/.test(data.password)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password must contain at least one number",
        path: ["password"],
      });
    }
    if (!/[^A-Za-z0-9]/.test(data.password)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Password must contain at least one special character",
        path: ["password"],
      });
    }
  }

  if (data.password !== data.confirmPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Passwords do not match",
      path: ["confirmPassword"],
    });
  }

  // When role is doctor, specialty and registration number are required
  if (data.role === 'doctor') {
    if (!data.specialty || data.specialty.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Specialty is required for doctors",
        path: ["specialty"],
      });
    }
    if (!data.registrationNumber || data.registrationNumber.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Registration number is required for doctors",
        path: ["registrationNumber"],
      });
    }
  }
});

type UserFormValues = z.infer<typeof userFormSchema>;

export function UsersManagement() {
  const { toast } = useToast();
  const { users, isLoading, error, fetchUsers, addUser, updateUser, removeUser, removeUserWithCascade } = useUsersStore();
  const [activeTab, setActiveTab] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isCompleteDeletionDialogOpen, setIsCompleteDeletionDialogOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "staff",
      specialty: "",
      registrationNumber: "",
    },
  });

  const filteredUsers = activeTab === "all" 
    ? users.filter(user => {
        const name = user.name || '';
        const email = user.email || '';
        const query = searchQuery.toLowerCase();
        return name.toLowerCase().includes(query) || email.toLowerCase().includes(query);
      })
    : users.filter(user => {
        const name = user.name || '';
        const email = user.email || '';
        const query = searchQuery.toLowerCase();
        return user.role === activeTab &&
          (name.toLowerCase().includes(query) || email.toLowerCase().includes(query));
      });

  const handleOpenAddDialog = () => {
    setEditingUser(null);
    form.reset({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      role: "staff",
      specialty: "",
      registrationNumber: "",
    });
    setIsAddUserDialogOpen(true);
  };

  const handleAddUser = async (data: UserFormValues) => {
    try {
      console.log('=== Starting user creation ===');
      console.log('Form data:', data);
      
      // First check if the current user is an admin
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        throw new Error('You must be logged in to add users');
      }

      console.log('Current user:', currentUser.id);

      // Get the current user's profile to check their role and hospital_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, hospital_id')
        .eq('id', currentUser.id)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        throw new Error('Failed to verify admin permissions');
      }

      console.log('Current user role:', profile.role);
      console.log('Current user hospital_id:', profile.hospital_id);

      if (profile.role !== 'admin') {
        throw new Error('Only administrators can add new users');
      }

      if (!profile.hospital_id) {
        throw new Error('No hospital context found. Please contact support.');
      }

      if (!data.password) {
        throw new Error('Password is required for new users');
      }

      // For doctors, ensure required fields are present
      if (data.role === 'doctor') {
        if (!data.specialty) {
          throw new Error('Specialty is required for doctors');
        }
        if (!data.registrationNumber) {
          throw new Error('Registration number is required for doctors');
        }
      }

      // Check if email already exists in THIS hospital
      const { data: existingUserInHospital, error: existingUserError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', data.email)
        .eq('hospital_id', profile.hospital_id)
        .maybeSingle();

      if (existingUserError) {
        console.error('Error checking existing user:', existingUserError);
      }

      if (existingUserInHospital) {
        throw new Error('A user with this email already exists in your hospital');
      }

      // Check if user exists in ANOTHER hospital (for multi-hospital doctors)
      const { data: existingUserOtherHospital, error: checkOtherError } = await supabase
        .from('profiles')
        .select('id, hospital_id')
        .eq('email', data.email)
        .neq('hospital_id', profile.hospital_id)
        .maybeSingle();

      if (checkOtherError && checkOtherError.code !== 'PGRST116') {
        console.warn('Error checking other hospitals:', checkOtherError);
      }

      if (existingUserOtherHospital) {
        // User exists in another hospital - add them to this hospital
        // Reuse existing auth user, just add doctor entry for this hospital
        console.log(`User ${data.email} exists in another hospital. Adding to this hospital.`);
        
        // Check if doctor entry already exists for this hospital
        const { data: existingDoctor } = await supabase
          .from('doctors')
          .select('id')
          .eq('id', existingUserOtherHospital.id)
          .eq('hospital_id', profile.hospital_id)
          .maybeSingle();

        if (existingDoctor) {
          throw new Error('This doctor is already added to your hospital');
        }

        if (data.role === 'doctor') {
          // Add doctor entry for this hospital
          const { error: doctorError } = await supabase
            .from('doctors')
            .insert({
              id: existingUserOtherHospital.id,
              name: data.name,
              specialization: data.specialty,
              registration_number: data.registrationNumber,
              status: 'available',
              hospital_id: profile.hospital_id,
              created_at: new Date().toISOString()
            });

          if (doctorError) {
            console.error('Error adding doctor to hospital:', doctorError);
            throw new Error(`Failed to add doctor: ${doctorError.message}`);
          }

          toast({
            title: 'Doctor added successfully',
            description: `${data.name} has been added to your hospital. They can login with their existing email and password.`,
          });

          setIsAddUserDialogOpen(false);
          fetchUsers();
          return; // Exit - no need to create auth user
        } else {
          // For staff, we can't easily add them to another hospital
          // as profiles table has one hospital_id per user
          throw new Error('This user already exists in another hospital. Staff members can only belong to one hospital. Doctors can be added to multiple hospitals.');
        }
      }

      console.log('Creating auth user with data:', {
        email: data.email,
        role: data.role,
        name: data.name,
        specialty: data.specialty,
        registrationNumber: data.registrationNumber
      });

      // Create the auth user
      // Set redirect URL for email confirmation (for admin-created users, we'll auto-confirm)
      const redirectUrl = getAuthRedirectUrl('/confirm-email');
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            role: data.role,
            name: data.name,
            hospital_id: profile.hospital_id,
            specialty: data.specialty || null,
            registration_number: data.registrationNumber || null
          },
          emailRedirectTo: redirectUrl // Even though we auto-confirm, set this for fallback
        }
      });

      if (authError) {
        console.error('Auth error:', authError);
        throw new Error(`Failed to create user: ${authError.message}`);
      }
      
      if (!authData.user) {
        throw new Error('No user data returned from signup');
      }

      console.log('Auth user created successfully:', authData.user.id);

      // Auto-confirm email for admin-created users (doctors/staff)
      // NOTE: Admin signup (SaaS-SignUp.tsx) still requires email confirmation
      // Only users created by admins get auto-confirmed
      // This requires running the auto_confirm_user_email.sql function in Supabase
      try {
        const { error: confirmError } = await supabase.rpc('auto_confirm_user_email', {
          user_uuid: authData.user.id
        });
        
        if (confirmError) {
          // 400 might mean function doesn't exist or wrong params
          // 404 means function doesn't exist
          // This is not critical for user creation, but user won't be able to login
          if (confirmError.code === '42883' || confirmError.code === 'PGRST301' || 
              confirmError.message?.includes('404') || confirmError.message?.includes('400')) {
            console.warn('⚠️ Auto-confirm function not available!');
            console.warn('⚠️ User created but will need manual email confirmation to login');
            console.warn('⚠️ Run database/fix_user_creation_rls.sql to create the function');
            console.warn('⚠️ Or run database/confirm_existing_user_emails.sql to fix existing users');
            
            // Show user-friendly warning
            toast({
              title: "User created",
              description: "User created successfully. Note: They will need email confirmation to login. Run the SQL script to auto-confirm admin-created users.",
              variant: "default",
            });
          } else {
            console.error('❌ Could not auto-confirm email:', confirmError);
            toast({
              title: "Warning",
              description: "User created but email confirmation failed. They may need manual confirmation to login.",
              variant: "default",
            });
          }
        } else {
          console.log('✅ Email auto-confirmed successfully (admin-created user)');
        }
      } catch (error: any) {
        // Non-critical error - user can still be created
        console.error('❌ Email auto-confirmation error:', error?.message || error);
        toast({
          title: "Warning",
          description: "User created but email auto-confirmation failed. Run the SQL script to enable it.",
          variant: "default",
        });
      }

      // Wait for the trigger to create the profile
      console.log('Waiting for profile trigger...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Verify the profile was created
      // Try multiple times with retry logic (trigger might take a moment)
      let newProfile = null;
      let profileCheckError = null;
      
      for (let attempt = 0; attempt < 3; attempt++) {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authData.user.id)
          .maybeSingle();

        if (!error || error.code === 'PGRST116') {
          // PGRST116 means no rows found - that's okay, we'll create it
          newProfile = data || null;
          profileCheckError = null;
          break;
        } else {
          profileCheckError = error;
          // If it's a 406 (RLS error), wait and retry
          if (error.code === '42501' || error.message?.includes('406')) {
            console.warn(`Profile fetch blocked by RLS (attempt ${attempt + 1}), retrying...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          } else {
            console.error('Profile check error:', error);
            break;
          }
        }
      }

      if (profileCheckError && profileCheckError.code !== 'PGRST116') {
        console.warn('Could not fetch profile, will attempt to create/update it:', profileCheckError);
      }

      console.log('Profile check result:', newProfile);

      // If profile doesn't exist, create it manually
      if (!newProfile) {
        console.log('Profile not found, attempting manual creation');
        const { error: manualProfileError } = await supabase
          .from('profiles')
          .insert([{
            id: authData.user.id,
            email: data.email,
            role: data.role,
            name: data.name,
            hospital_id: profile.hospital_id, // Add hospital_id from current admin
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);

        if (manualProfileError) {
          // If it's a duplicate key error, the profile was created by trigger
          if (manualProfileError.code === '23505') {
            console.log('Profile already exists (created by trigger), updating it...');
            
            // Try to update the profile (retry if RLS blocks)
            let updateSuccess = false;
            for (let attempt = 0; attempt < 3; attempt++) {
              const { error: updateError } = await supabase
                .from('profiles')
                .update({
                  name: data.name,
                  hospital_id: profile.hospital_id,
                  role: data.role,
                  updated_at: new Date().toISOString()
                })
                .eq('id', authData.user.id);
              
              if (!updateError) {
                console.log('Profile updated successfully');
                updateSuccess = true;
                break;
              } else if (updateError.code === '42501' || updateError.message?.includes('406')) {
                console.warn(`Profile update blocked by RLS (attempt ${attempt + 1}), retrying...`);
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
              } else {
                console.error('Error updating profile:', updateError);
                throw new Error(`Failed to update user profile: ${updateError.message}`);
              }
            }
            
            if (!updateSuccess) {
              console.warn('Could not update profile due to RLS, but profile exists. User creation may still work.');
            }
          } else {
            console.error('Manual profile creation error:', manualProfileError);
            throw new Error(`Failed to create user profile: ${manualProfileError.message}`);
          }
        } else {
          console.log('Manual profile creation successful');
        }
      } else {
        // Profile exists, update it with hospital_id and other details if needed
        console.log('Profile exists, updating if needed');
        const updates: any = {
          updated_at: new Date().toISOString()
        };

        if (!newProfile.hospital_id) {
          updates.hospital_id = profile.hospital_id;
        }
        if (!newProfile.name && data.name) {
          updates.name = data.name;
        }
        if (newProfile.role !== data.role) {
          updates.role = data.role;
        }

        if (Object.keys(updates).length > 1) { // More than just updated_at
          console.log('Updating profile with:', updates);
          const { error: updateError } = await supabase
            .from('profiles')
            .update(updates)
            .eq('id', authData.user.id);
          
          if (updateError) {
            console.error('Error updating profile:', updateError);
            throw new Error(`Failed to update user profile: ${updateError.message}`);
          }
          console.log('Profile updated successfully');
          
          // Verify the update worked
          const { data: verifyProfile } = await supabase
            .from('profiles')
            .select('hospital_id, name, role')
            .eq('id', authData.user.id)
            .single();
          console.log('Verified profile after update:', verifyProfile);
        } else {
          // Even if no updates needed, ensure hospital_id is set
          if (!newProfile.hospital_id) {
            console.log('Profile missing hospital_id, setting it now...');
            const { error: updateError } = await supabase
              .from('profiles')
              .update({ hospital_id: profile.hospital_id })
              .eq('id', authData.user.id);
            
            if (updateError) {
              console.error('Error setting hospital_id:', updateError);
            } else {
              console.log('hospital_id set successfully');
            }
          }
        }
      }

      // If the user is a doctor, create an entry in the doctors table
      if (data.role === 'doctor') {
        try {
          // Check if doctor already exists for this hospital
          const { data: existingDoctor, error: checkError } = await supabase
            .from('doctors')
            .select('id, hospital_id')
            .eq('id', authData.user.id)
            .eq('hospital_id', profile.hospital_id)
            .maybeSingle();

          if (checkError && checkError.code !== 'PGRST116') {
            console.error('Error checking existing doctor:', checkError);
          }

          if (existingDoctor) {
            // Doctor already exists for this hospital - update instead
            console.log('Doctor already exists, updating...');
            const { error: updateError } = await supabase
              .from('doctors')
              .update({
                name: data.name,
                specialization: data.specialty,
                registration_number: data.registrationNumber,
                status: 'available'
              })
              .eq('id', authData.user.id)
              .eq('hospital_id', profile.hospital_id);

            if (updateError) {
              console.error('Error updating doctor:', updateError);
              throw new Error(`Failed to update doctor profile: ${updateError.message}`);
            }
            console.log('Doctor profile updated successfully');
          } else {
            // Doctor doesn't exist - create new entry
            const doctorData = {
              id: authData.user.id,
              name: data.name,
              specialization: data.specialty,
              registration_number: data.registrationNumber,
              status: 'available',
              hospital_id: profile.hospital_id,
              created_at: new Date().toISOString()
            };

            console.log('Creating doctor profile with data:', doctorData);

            const { error: doctorError } = await supabase
              .from('doctors')
              .insert([doctorData]);

            if (doctorError) {
              // If it's a duplicate key error, try to update instead
              if (doctorError.code === '23505') {
                console.log('Doctor duplicate key error, attempting update instead...');
                const { error: updateError } = await supabase
                  .from('doctors')
                  .update({
                    name: data.name,
                    specialization: data.specialty,
                    registration_number: data.registrationNumber,
                    status: 'available'
                  })
                  .eq('id', authData.user.id)
                  .eq('hospital_id', profile.hospital_id);

                if (updateError) {
                  console.error('Error updating doctor after duplicate key:', updateError);
                  throw new Error(`Failed to create/update doctor profile: ${updateError.message}`);
                }
                console.log('Doctor profile updated successfully after duplicate key');
              } else {
                console.error('Doctor creation error:', doctorError);
                throw new Error(`Failed to create doctor profile: ${doctorError.message}`);
              }
            } else {
              console.log('Doctor profile created successfully');
            }
          }
        } catch (doctorError: any) {
          console.error('Error in doctor creation:', doctorError);
          toast({
            title: "Error creating doctor profile",
            description: doctorError.message,
            variant: "destructive",
          });
          // Don't throw here, as the user is already created
          return;
        }
      }

      console.log('User creation completed successfully');
      setIsAddUserDialogOpen(false);
      form.reset();
      
      // Refresh the users list
      await fetchUsers();
      
      toast({
        title: "User added",
        description: "The user has been successfully added.",
      });
    } catch (error: any) {
      console.error('Error adding user:', error);
      toast({
        title: "Error adding user",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEditUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setEditingUser(user);
      form.reset({
        name: user.name || "",
        email: user.email || "",
        password: "",
        confirmPassword: "",
        role: user.role || "staff",
        specialty: user.specialty || "",
        registrationNumber: user.registrationNumber || "",
      });
      setIsAddUserDialogOpen(true);
    }
  };

  const handleUpdateUser = async (data: UserFormValues) => {
    if (!editingUser) return;

    try {
      // First check if the current user is an admin
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        throw new Error('You must be logged in to update users');
      }

      // Get the current user's profile to check their role and hospital_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, hospital_id')
        .eq('id', currentUser.id)
        .single();

      if (profileError) {
        throw new Error('Failed to verify admin permissions');
      }

      if (profile.role !== 'admin') {
        throw new Error('Only administrators can update users');
      }

      if (!profile.hospital_id) {
        throw new Error('No hospital context found');
      }

      // First, check if the user being updated is in the same hospital
      const { data: targetProfile, error: targetError } = await supabase
        .from('profiles')
        .select('hospital_id')
        .eq('id', editingUser.id)
        .single();

      if (targetError) {
        throw new Error(`Failed to find user profile: ${targetError.message}`);
      }

      // If target user doesn't have hospital_id, set it
      const updateData: any = {
        name: data.name || null,
        email: data.email,
        role: data.role,
      };

      // Only set hospital_id if it's missing or if user is in same hospital
      if (!targetProfile.hospital_id) {
        updateData.hospital_id = profile.hospital_id;
        console.log('Setting hospital_id for user:', editingUser.id);
      } else if (targetProfile.hospital_id !== profile.hospital_id) {
        throw new Error('Cannot update user from different hospital');
      }

      // Update the user profile in Supabase
      // Note: specialty and registration_number don't exist in profiles table
      // They only exist in doctors table
      let query = supabase
        .from('profiles')
        .update(updateData)
        .eq('id', editingUser.id);

      // Only filter by hospital_id if it exists
      if (targetProfile.hospital_id) {
        query = query.eq('hospital_id', profile.hospital_id);
      }

      const { error: updateError } = await query;

      if (updateError) {
        console.error('Profile update error:', updateError);
        throw updateError;
      }

      // If the user is a doctor, update the doctors table
      if (data.role === 'doctor') {
        const { error: doctorError } = await supabase
          .from('doctors')
          .update({
            name: data.name || null,
            specialization: data.specialty || null,
            registration_number: data.registrationNumber || null
          })
          .eq('id', editingUser.id)
          .eq('hospital_id', profile.hospital_id); // Ensure multi-tenant isolation

        if (doctorError) {
          console.error('Error updating doctor:', doctorError);
          // If doctor entry doesn't exist, create it
          if (doctorError.code === 'PGRST116' || doctorError.message?.includes('not found')) {
            const { error: createError } = await supabase
              .from('doctors')
              .insert({
                id: editingUser.id,
                name: data.name || null,
                specialization: data.specialty || null,
                registration_number: data.registrationNumber || null,
                status: 'available',
                hospital_id: profile.hospital_id
              });
            
            if (createError) {
              console.error('Error creating doctor entry:', createError);
              throw new Error(`Failed to update doctor entry: ${createError.message}`);
            }
          } else {
            throw doctorError;
          }
        }
      }

      // Update local state
      const updatedUser: User = {
        ...editingUser,
        name: data.name,
        email: data.email,
        role: data.role,
        specialty: data.specialty,
        registrationNumber: data.registrationNumber,
      };

      await updateUser(updatedUser);
      setIsAddUserDialogOpen(false);
      setEditingUser(null);
      form.reset();

      toast({
        title: "User updated",
        description: "The user has been successfully updated.",
      });
    } catch (error: any) {
      toast({
        title: "Error updating user",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setUserToDelete(user);
      setIsCompleteDeletionDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await removeUserWithCascade(userToDelete.id);
      toast({
        title: "User deactivated",
        description: `The user ${userToDelete.name} has been successfully deactivated and can no longer login.`,
      });
    } catch (error: any) {
      toast({
        title: "Error deleting user",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!userToDelete) return;
    
    try {
      await removeUserWithCascade(userToDelete.id);
      
      // Refresh users list to ensure UI is updated
      await fetchUsers();
      
      toast({
        title: "User deactivated",
        description: `The user ${userToDelete.name} has been successfully deactivated.`,
      });
      
      // Close dialog
      setIsCompleteDeletionDialogOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      toast({
        title: "Error deactivating user",
        description: error.message,
        variant: "destructive",
      });
      // Refresh list even on error
      await fetchUsers();
    }
  };

  const handleConfirmComplete = async () => {
    if (!userToDelete) return;
    
    const userIdToDelete = userToDelete.id;
    const userNameToDelete = userToDelete.name;
    
    try {
      const result = await removeUserCompletely(userIdToDelete);
      if (result.success) {
        // Wait a moment for database to commit
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Immediately remove from local state (optimistic update)
        removeUser(userIdToDelete);
        
        // Then refresh from database to ensure consistency
        await fetchUsers();
        
        // Verify user is actually gone (check after refresh)
        setTimeout(async () => {
          await fetchUsers(); // Refresh again to verify
          const store = useUsersStore.getState();
          const userStillExists = store.users.find(u => u.id === userIdToDelete);
          if (userStillExists) {
            console.error('⚠️ User still exists after deletion! This may be due to:');
            console.error('   1. User still exists in auth.users (trigger recreating profile)');
            console.error('   2. RLS policies blocking deletion');
            console.error('   3. Hospital ID mismatch');
            toast({
              title: "Warning: User may still exist",
              description: `The user was deleted but may still exist in auth.users. If they reappear, you may need to delete from auth.users manually using SQL.`,
              variant: "default",
            });
          }
        }, 500);
        
        // Check if auth.users deletion still needed
        if (result.message?.includes('auth.users')) {
          toast({
            title: "User deleted from application tables",
            description: `The user ${userNameToDelete} has been permanently deleted from all application tables. Note: User still exists in auth.users - if they reappear, delete from auth.users manually.`,
            variant: "default",
          });
        } else {
          toast({
            title: "User completely deleted",
            description: `The user ${userNameToDelete} has been completely removed from all tables including authentication.`,
          });
        }
        
        // Close dialog after a short delay to ensure state updates
        setTimeout(() => {
          setIsCompleteDeletionDialogOpen(false);
          setUserToDelete(null);
        }, 100);
      } else {
        throw new Error(result.error || 'Failed to delete user');
      }
    } catch (error: any) {
      console.error('Deletion error:', error);
      toast({
        title: "Error deleting user",
        description: error.message || 'Failed to delete user. The user may still exist in auth.users or RLS policies may be blocking deletion.',
        variant: "destructive",
      });
      // Refresh list even on error to show current state
      await fetchUsers();
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "admin":
        return <Shield className="h-4 w-4" />;
      case "doctor":
        return <UserIcon className="h-4 w-4" />;
      default:
        return <UserIcon className="h-4 w-4" />;
    }
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name || name.trim() === '') {
      return '?'; // Default fallback for missing names
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .filter(Boolean) // Filter out empty strings
      .join("")
      .toUpperCase()
      .slice(0, 2); // Limit to 2 initials max
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Users Management</CardTitle>
          <CardDescription>
            Manage your clinic's users and their roles
          </CardDescription>
        </div>
        <Button onClick={handleOpenAddDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="admin">Admins</TabsTrigger>
              <TabsTrigger value="doctor">Doctors</TabsTrigger>
              <TabsTrigger value="staff">Staff</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-4">
            Loading users...
          </div>
        ) : filteredUsers.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                  <TableHead>Email Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar>
                        <AvatarImage src={user.avatarUrl} />
                        <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{user.name || 'No Name'}</div>
                        <div className="text-sm text-muted-foreground">{user.email || 'No Email'}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getRoleIcon(user.role)}
                      <span className="capitalize">{user.role}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={
                        user.status === "verified" 
                          ? "default" 
                          : user.status === "pending"
                          ? "secondary"
                          : "destructive"
                      }
                      className={
                        user.status === "verified"
                          ? "bg-green-100 text-green-800 hover:bg-green-100"
                          : user.status === "pending"
                          ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"
                          : "bg-red-100 text-red-800 hover:bg-red-100"
                      }
                    >
                      {(() => {
                        if (user.status === "verified") return "✅ Verified";
                        if (user.status === "pending") return "⏳ Pending";
                        if (user.status === "unverified") return "❌ Unverified";
                        return "⏳ Pending";
                      })()}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditUser(user.id)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDeleteUser(user.id)}>
                          <Trash className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            No users found
          </div>
        )}

        <Dialog open={isAddUserDialogOpen} onOpenChange={setIsAddUserDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
              <DialogDescription>
                {editingUser
                  ? "Update the user's information below."
                  : "Fill in the user's information below to create a new account."}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(editingUser ? handleUpdateUser : handleAddUser)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" autoComplete="email" value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {!editingUser && (
                  <>
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                {...field}
                                type={showPassword ? "text" : "password"}
                                autoComplete="new-password"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                {...field}
                                type={showConfirmPassword ? "text" : "password"}
                                autoComplete="new-password"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              >
                                {showConfirmPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="doctor">Doctor</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {form.watch("role") === "doctor" && (
                  <>
                    <FormField
                      control={form.control}
                      name="specialty"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Specialty</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="registrationNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Registration Number</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                <DialogFooter>
                  <Button type="submit">
                    {editingUser ? "Update User" : "Add User"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <CompleteDeletionDialog
          isOpen={isCompleteDeletionDialogOpen}
          onClose={() => {
            setIsCompleteDeletionDialogOpen(false);
            setUserToDelete(null);
          }}
          onConfirmDeactivate={handleConfirmDeactivate}
          onConfirmComplete={handleConfirmComplete}
          userToDelete={userToDelete}
        />
      </CardContent>
    </Card>
  );
}
